import moment from "moment";
import { PROCEDURES } from "@/services/palliative/susProductionApi";

const LINES_PER_SHEET = 20;
const LAYOUT_VERSION = "LILA-POC";

const plain = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^\x20-\x7E]/g, "")
    .toUpperCase();

const text = (value, size) => plain(value).slice(0, size).padEnd(size, " ");
const number = (value, size) => String(value || 0).replace(/\D/g, "").slice(-size).padStart(size, "0");

function ageAt(birthDate, date) {
  if (!birthDate) return 0;
  return Math.max(0, moment(date).diff(moment(birthDate), "years"));
}

function sheetAndSequence(index) {
  return {
    sheet: Math.floor(index / LINES_PER_SHEET) + 1,
    sequence: (index % LINES_PER_SHEET) + 1
  };
}

function consolidatedRows(items) {
  const groups = {};
  items.forEach((item) => {
    const key = `${item.professional.cbo}|${item.code}`;
    groups[key] = groups[key] || { cbo: item.professional.cbo, code: item.code, quantity: 0 };
    groups[key].quantity += item.quantity;
  });
  return Object.values(groups);
}

const instrumentOf = (item) => item.instrument || (PROCEDURES[item.code] || {}).instrument;

export function buildBpaFile(team, competence, items) {
  const individual = items.filter((item) => instrumentOf(item) === "BPA-I");
  const consolidated = consolidatedRows(items.filter((item) => instrumentOf(item) === "BPA-C"));

  const consolidatedLines = consolidated.map((row, index) => {
    const { sheet, sequence } = sheetAndSequence(index);
    return [
      "02",
      number(team.cnes, 7),
      number(competence, 6),
      number(row.cbo, 6),
      number(sheet, 3),
      number(sequence, 2),
      number(row.code, 10),
      number(0, 3),
      number(row.quantity, 6),
      "BPA"
    ].join("");
  });

  const individualLines = individual.map((item, index) => {
    const { sheet, sequence } = sheetAndSequence(index);
    return [
      "03",
      number(team.cnes, 7),
      number(competence, 6),
      number(item.professional.cns, 15),
      number(item.professional.cbo, 6),
      moment(item.date).format("YYYYMMDD"),
      number(sheet, 3),
      number(sequence, 2),
      number(item.code, 10),
      number(item.patient.cns, 15),
      text(item.patient.sex, 1),
      number(team.ibge, 6),
      text(String(item.patient.cid || "").replace(".", ""), 4),
      number(ageAt(item.patient.birthDate, item.date), 3),
      number(item.quantity, 6),
      "01",
      text("", 13),
      "BPA",
      text(item.patient.name, 30),
      item.patient.birthDate ? moment(item.patient.birthDate).format("YYYYMMDD") : number(0, 8)
    ].join("");
  });

  const bodyLines = [...consolidatedLines, ...individualLines];
  const sheets =
    Math.ceil(consolidated.length / LINES_PER_SHEET) + Math.ceil(individual.length / LINES_PER_SHEET);
  const control =
    (items.reduce((sum, item) => sum + Number(item.code) + item.quantity, 0) % 1111) + 1111;

  const header = [
    "01",
    "#BPA#",
    number(competence, 6),
    number(bodyLines.length, 6),
    number(sheets, 6),
    number(control, 4),
    text(team.name, 30),
    text(team.acronym, 6),
    number(team.cnpj, 14),
    text(team.destination, 40),
    "M",
    text(LAYOUT_VERSION, 10)
  ].join("");

  return {
    content: [header, ...bodyLines].join("\r\n") + "\r\n",
    fileName: `BPA_${team.cnes}_${competence}.txt`,
    individualCount: individualLines.length,
    consolidatedCount: consolidatedLines.length
  };
}

export function downloadTextFile(fileName, content) {
  const blob = new Blob([content], { type: "text/plain;charset=us-ascii" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
